import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  FaUsers, 
  FaUserGraduate, 
  FaBook, 
  FaClipboardCheck, 
  FaSpinner, 
  FaChartBar,
  FaChartPie,
  FaCalendarAlt,
  FaTrophy
} from 'react-icons/fa';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line
} from 'recharts';
import axios from 'axios';
import config from '../config/api';

const AdminDashboard = () => {
  const { user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [monthlyStats, setMonthlyStats] = useState(null);
  const [subjectStats, setSubjectStats] = useState(null);
  const [performanceStats, setPerformanceStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const colors = {
    primary: '#3B82F6',
    secondary: '#10B981', 
    warning: '#F59E0B',
    danger: '#EF4444',
    purple: '#8B5CF6',
    teal: '#14B8A6'
  };

  const pieColors = [colors.primary, colors.secondary, colors.warning, colors.danger, colors.purple, colors.teal];

  useEffect(() => {
    const fetchAllStats = async () => {
      try {
        setLoading(true);
        
        const [overviewRes, monthlyRes, subjectsRes, performanceRes] = await Promise.all([
          axios.get(`${config.apiURL}/admin/stats/overview`),
          axios.get(`${config.apiURL}/admin/stats/monthly`),
          axios.get(`${config.apiURL}/admin/stats/subjects`),
          axios.get(`${config.apiURL}/admin/stats/performance`)
        ]);

        setOverview(overviewRes.data);
        setMonthlyStats(monthlyRes.data);
        setSubjectStats(subjectsRes.data);
        setPerformanceStats(performanceRes.data);
      } catch (err) {
        console.error('Erro ao carregar estatísticas:', err);
        setError('Erro ao carregar dados administrativos');
      } finally {
        setLoading(false);
      }
    };

    fetchAllStats();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center h-64">
            <div className="text-center">
              <FaSpinner className="animate-spin h-12 w-12 text-blue-600 mx-auto mb-4" />
              <p className="text-gray-600">Carregando painel administrativo...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Erro</h3>
                <div className="mt-2 text-sm text-red-700">{error}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            Painel Administrativo
          </h1>
          <p className="mt-2 text-gray-600">
            Olá, {user?.name}! Aqui estão as estatísticas da plataforma (dados agregados - LGPD compliant)
          </p>
        </div>

        {/* Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <FaUsers className="h-6 w-6 text-blue-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Professores Total
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {overview?.teachers?.total || 0}
                    </dd>
                    <dd className="text-sm text-gray-500">
                      {overview?.teachers?.verified || 0} verificados
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <FaUserGraduate className="h-6 w-6 text-green-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Estudantes Total
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {overview?.students?.total || 0}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <FaBook className="h-6 w-6 text-yellow-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Lições Criadas
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {overview?.homeworks?.total || 0}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <FaClipboardCheck className="h-6 w-6 text-purple-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Submissões Total
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {overview?.submissions?.total || 0}
                    </dd>
                    <dd className="text-sm text-gray-500">
                      {overview?.submissions?.graded || 0} corrigidas
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Charts Row 1 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Monthly Growth Chart */}
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center mb-4">
              <FaChartBar className="h-5 w-5 text-blue-600 mr-2" />
              <h3 className="text-lg font-medium text-gray-900">
                Crescimento Mensal ({monthlyStats?.year})
              </h3>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={monthlyStats?.teachers || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="monthName" tick={{ fontSize: 12 }} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="count" fill={colors.primary} name="Professores" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Performance Distribution */}
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center mb-4">
              <FaChartPie className="h-5 w-5 text-green-600 mr-2" />
              <h3 className="text-lg font-medium text-gray-900">
                Distribuição de Notas
              </h3>
            </div>
            {performanceStats?.grade_distribution?.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={performanceStats.grade_distribution}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ grade_range, count, percent }) => 
                      `${grade_range}: ${count} (${(percent * 100).toFixed(1)}%)`
                    }
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="count"
                  >
                    {performanceStats.grade_distribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={pieColors[index % pieColors.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-64 text-gray-500">
                <div className="text-center">
                  <FaTrophy className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                  <p>Nenhuma nota registrada ainda</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Charts Row 2 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Students Growth */}
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center mb-4">
              <FaUserGraduate className="h-5 w-5 text-green-600 mr-2" />
              <h3 className="text-lg font-medium text-gray-900">
                Crescimento de Estudantes
              </h3>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={monthlyStats?.students || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="monthName" tick={{ fontSize: 12 }} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="count" 
                  stroke={colors.secondary} 
                  strokeWidth={2}
                  name="Estudantes"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Subject Statistics */}
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center mb-4">
              <FaBook className="h-5 w-5 text-yellow-600 mr-2" />
              <h3 className="text-lg font-medium text-gray-900">
                Estatísticas por Matéria
              </h3>
            </div>
            {subjectStats?.subjects?.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={subjectStats.subjects.slice(0, 6)}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="subject" tick={{ fontSize: 12 }} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="homeworks_count" fill={colors.warning} name="Lições" />
                  <Bar dataKey="submissions_count" fill={colors.teal} name="Submissões" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-64 text-gray-500">
                <div className="text-center">
                  <FaBook className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                  <p>Nenhuma matéria cadastrada ainda</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Performance Summary */}
        {performanceStats && (
          <div className="bg-white p-6 rounded-lg shadow mb-8">
            <div className="flex items-center mb-4">
              <FaTrophy className="h-5 w-5 text-purple-600 mr-2" />
              <h3 className="text-lg font-medium text-gray-900">
                Resumo de Desempenho
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600">
                  {performanceStats.average_grade}
                </div>
                <div className="text-sm text-gray-500">Nota Média Geral</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600">
                  {performanceStats.total_graded}
                </div>
                <div className="text-sm text-gray-500">Total de Avaliações</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-purple-600">
                  {overview?.submissions?.total && overview?.submissions?.graded 
                    ? Math.round((overview.submissions.graded / overview.submissions.total) * 100)
                    : 0}%
                </div>
                <div className="text-sm text-gray-500">Taxa de Correção</div>
              </div>
            </div>
          </div>
        )}

        {/* Footer Note */}
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">📊 Conformidade LGPD</h3>
              <div className="mt-2 text-sm text-blue-700">
                Todos os dados apresentados são estatísticas agregadas e anônimas. 
                Nenhuma informação pessoal (nomes, emails, etc.) é exibida neste painel.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard; 